import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, ReferenceLine, ComposedChart, Area,
} from 'recharts';
import toast from 'react-hot-toast';
import apiClient from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { RangeSelector, useTimeRange } from '../data/TimeRange';

const KG_TO_LB = 2.20462;

// One brand hue per metric — every chart here is a SINGLE series (its own colour +
// text label), so there's no categorical-adjacency to validate. Warm gold/green/red
// family only; no teal/blue/purple.
const COLORS = {
  weight: '#FFB800',
  movingAvg: '#10B981',
  bmi: '#B87D00',
  bodyFat: '#F0791A',
  muscle: '#16A34A',
  water: '#EAB308',
  bone: '#A8A29E',
  visceral: '#DC2626',
};

const convertWeight = (value, unit) => {
  if (value == null) return null;
  return unit === 'lb' ? +(value * KG_TO_LB).toFixed(1) : +value.toFixed(1);
};

const fmtVal = (m, v) => (v == null ? '--' : `${v}${m.suffix}`);

// A tiny axis-less trend line for the metric cards. Reads the same chartData rows
// as the big chart, so the card and its expansion can never disagree.
const Sparkline = ({ data, dataKey, color }) => (
  <ResponsiveContainer width="100%" height="100%">
    <LineChart data={data} margin={{ top: 4, right: 2, bottom: 2, left: 2 }}>
      <YAxis hide domain={['dataMin', 'dataMax']} />
      <Line
        dataKey={dataKey}
        stroke={color}
        strokeWidth={2}
        dot={false}
        type="monotone"
        connectNulls
        isAnimationActive={false}
      />
    </LineChart>
  </ResponsiveContainer>
);

const MetricTooltip = ({ active, payload, metric }) => {
  if (!active || !payload?.length || !metric) return null;
  const d = payload[0]?.payload;
  if (!d) return null;
  const dateLabel = new Date(d.date + 'T00:00:00').toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
  });
  return (
    <div className="bg-raised border border-line rounded-lg p-3 text-sm">
      <p className="font-medium text-fg mb-2">{dateLabel}</p>
      <div className="space-y-1">
        <p style={{ color: metric.color }}>
          {metric.label}: <span className="font-medium">{fmtVal(metric, d[metric.dataKey])}</span>
        </p>
        {metric.showMovingAvg && d.movingAvg != null && (
          <p style={{ color: COLORS.movingAvg }}>
            Moving avg: <span className="font-medium">{fmtVal(metric, d.movingAvg)}</span>
          </p>
        )}
      </div>
    </div>
  );
};

const WeightChart = () => {
  const { user, refreshUser } = useAuth();
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(true);
  const unit = user?.weight_unit || 'kg';
  const { inRange, spanDays, selectorProps } = useTimeRange();
  // Which metric the big chart is showing. The cards below double as the selector.
  const [selected, setSelected] = useState('weight');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await apiClient.get('/api/fitbit/weight');
        if (response.status === 200) setData(response.data);
      } catch (error) {
        toast.error('Failed to load weight data');
      } finally {
        setLoading(false);
      }
    };
    const setData = (d) => setAllData(d);
    fetchData();
  }, []);

  const toggleUnit = async () => {
    const newUnit = unit === 'kg' ? 'lb' : 'kg';
    try {
      await apiClient.patch('/api/auth/weight-unit', { weight_unit: newUnit });
      await refreshUser();
    } catch {
      toast.error('Failed to save weight unit preference');
    }
  };

  const filteredData = useMemo(() => {
    if (!allData.length) return [];
    return allData.filter(d => inRange(d.date));
  }, [allData, inRange]);

  const stats = useMemo(() => {
    if (!filteredData.length) return null;
    const weightVals = filteredData.filter(d => d.weight != null).map(d => convertWeight(d.weight, unit));
    return {
      avgWeight: weightVals.length ? +(weightVals.reduce((a, b) => a + b, 0) / weightVals.length).toFixed(1) : null,
      totalEntries: filteredData.length,
    };
  }, [filteredData, unit]);

  // One row per day carrying every metric, so a sparkline and the big chart read
  // the same source. Muscle % and body water % are derived from the raw kg values
  // (unit-independent ratios); weight/water/bone are converted to the display unit.
  const chartData = useMemo(() => {
    if (!filteredData.length) return [];

    const windowSize = filteredData.length > 90 ? 14 : 7;

    return filteredData.map((d, i) => {
      const window = filteredData.slice(Math.max(0, i - windowSize + 1), i + 1);
      const avgVals = window.filter(w => w.weight != null).map(w => convertWeight(w.weight, unit));
      const movingAvg = avgVals.length >= 2
        ? +(avgVals.reduce((a, b) => a + b, 0) / avgVals.length).toFixed(1)
        : null;

      return {
        date: d.date,
        weight: d.weight != null ? convertWeight(d.weight, unit) : null,
        movingAvg,
        bmi: d.bmi != null ? +d.bmi.toFixed(1) : null,
        bodyFat: d.body_fat != null ? +d.body_fat.toFixed(1) : null,
        musclePct: (d.muscle_mass != null && d.weight)
          ? +((d.muscle_mass / d.weight) * 100).toFixed(1) : null,
        water: d.hydration != null ? convertWeight(d.hydration, unit) : null,
        bone: d.bone_mass != null ? convertWeight(d.bone_mass, unit) : null,
        visceral: d.visceral_fat != null ? d.visceral_fat : null,
      };
    });
  }, [filteredData, unit]);

  // The metric catalog, filtered to what actually has data. Each is one series with
  // its own colour, latest value, and average — the card shows these; the big chart
  // renders the same dataKey when selected.
  const metrics = useMemo(() => {
    const lastVal = (key) => {
      for (let i = chartData.length - 1; i >= 0; i--) {
        if (chartData[i][key] != null) return chartData[i][key];
      }
      return null;
    };
    const avgVal = (key) => {
      const vs = chartData.map((d) => d[key]).filter((v) => v != null);
      return vs.length ? +(vs.reduce((a, b) => a + b, 0) / vs.length).toFixed(1) : null;
    };
    const defs = [
      { key: 'weight', dataKey: 'weight', label: 'Weight', color: COLORS.weight, suffix: ` ${unit}`, pad: 2, showMovingAvg: true },
      { key: 'bmi', dataKey: 'bmi', label: 'BMI', color: COLORS.bmi, suffix: '', pad: 1,
        refLines: [{ y: 18.5, c: '#22c55e' }, { y: 25, c: '#eab308' }, { y: 30, c: '#ef4444' }] },
      { key: 'bodyFat', dataKey: 'bodyFat', label: 'Body fat', color: COLORS.bodyFat, suffix: '%', pad: 2 },
      { key: 'muscle', dataKey: 'musclePct', label: 'Muscle', color: COLORS.muscle, suffix: '%', pad: 2 },
      { key: 'water', dataKey: 'water', label: 'Body water', color: COLORS.water, suffix: ` ${unit}`, pad: 2 },
      { key: 'bone', dataKey: 'bone', label: 'Bone mass', color: COLORS.bone, suffix: ` ${unit}`, pad: 1 },
      { key: 'visceral', dataKey: 'visceral', label: 'Visceral fat', color: COLORS.visceral, suffix: '', pad: 1, note: 'lower is better' },
    ];
    return defs
      .map((m) => ({ ...m, latest: lastVal(m.dataKey), avg: avgVal(m.dataKey) }))
      .filter((m) => m.latest != null);
  }, [chartData, unit]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-fg"></div>
      </div>
    );
  }

  if (!allData.length) {
    return (
      <Card>
        <CardContent className="py-12">
          <p className="text-center text-fg-2">
            No weight data yet. Weigh in on a connected scale (or sync Fitbit) to see it here.
          </p>
        </CardContent>
      </Card>
    );
  }

  const formatDate = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    const days = spanDays;
    if (days && days <= 90) {
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
    return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  };

  const tickInterval = Math.max(0, Math.ceil(chartData.length / 8) - 1);
  const metric = metrics.find((m) => m.key === selected) || metrics[0] || null;

  return (
    <div className="space-y-6">
      {/* Range Selector + Unit Toggle */}
      <RangeSelector
        {...selectorProps}
        right={(
          <div className="flex shrink-0 items-center gap-3">
            <span className="text-sm text-fg-2">
              {stats?.totalEntries} entries
            </span>
            <button
              onClick={toggleUnit}
              className="px-2.5 py-1.5 text-sm font-medium rounded-md border border-line-strong bg-raised text-fg-2 hover:bg-elev transition-colors"
            >
              {unit === 'kg' ? 'kg → lb' : 'lb → kg'}
            </button>
          </div>
        )}
      />

      {/* Metric cards — latest value + sparkline. Tap one to chart it in full below. */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {metrics.map((m) => {
          const isSel = metric && m.key === metric.key;
          return (
            <button
              key={m.key}
              type="button"
              onClick={() => setSelected(m.key)}
              aria-pressed={isSel}
              className={`text-left rounded-xl border p-3 transition-colors ${
                isSel
                  ? 'border-line-strong bg-elev'
                  : 'border-line hover:bg-elev '
              }`}
            >
              <p className="text-sm text-fg-2">{m.label}</p>
              <p className="text-2xl font-medium" style={{ color: m.color }}>{fmtVal(m, m.latest)}</p>
              <div className="mt-1 h-9">
                <Sparkline data={chartData} dataKey={m.dataKey} color={m.color} />
              </div>
              <p className="mt-1 text-sm text-fg-3">
                {m.key === 'visceral' ? m.note : (m.avg != null ? `Avg ${fmtVal(m, m.avg)}` : '')}
              </p>
            </button>
          );
        })}
      </div>

      {/* The selected metric, charted in full — crosshair tooltip, one axis, the title
          names the single series so no legend is needed. */}
      {metric && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>{metric.label}</CardTitle>
              <div className="text-sm text-fg-3">
                {metric.avg != null ? `Avg ${fmtVal(metric, metric.avg)}` : ''}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={320}>
              <ComposedChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#6b7280" opacity={0.15} />
                <XAxis
                  dataKey="date"
                  tickFormatter={formatDate}
                  interval={tickInterval}
                  tick={{ fontSize: 14, fill: '#9ca3af' }}
                  axisLine={{ stroke: '#4b5563' }}
                  tickLine={{ stroke: '#4b5563' }}
                />
                <YAxis
                  domain={[`dataMin - ${metric.pad}`, `dataMax + ${metric.pad}`]}
                  tickFormatter={(v) => `${v}${metric.suffix === '%' ? '%' : ''}`}
                  tick={{ fontSize: 14, fill: '#9ca3af' }}
                  axisLine={{ stroke: '#4b5563' }}
                  tickLine={{ stroke: '#4b5563' }}
                  width={48}
                />
                <Tooltip content={<MetricTooltip metric={metric} />} />
                {(metric.refLines || []).map((r) => (
                  <ReferenceLine key={r.y} y={r.y} stroke={r.c} strokeDasharray="3 3" />
                ))}
                {metric.key === 'weight' && stats?.avgWeight && (
                  <ReferenceLine
                    y={stats.avgWeight}
                    stroke="#9ca3af"
                    strokeDasharray="3 3"
                    label={{ value: `Avg ${stats.avgWeight}`, position: 'insideTopLeft', fill: '#9ca3af', fontSize: 14 }}
                  />
                )}
                <Area
                  dataKey={metric.dataKey}
                  stroke={metric.color}
                  strokeWidth={2}
                  fill={metric.color}
                  fillOpacity={0.06}
                  type="monotone"
                  dot={chartData.length <= 60 ? { r: 3, fill: metric.color } : false}
                  connectNulls
                />
                {metric.showMovingAvg && (
                  <Line
                    dataKey="movingAvg"
                    stroke={COLORS.movingAvg}
                    strokeWidth={2}
                    dot={false}
                    type="monotone"
                    connectNulls
                    strokeDasharray="5 3"
                  />
                )}
              </ComposedChart>
            </ResponsiveContainer>
            {metric.key === 'bmi' && (
              <div className="flex gap-4 mt-2 text-sm text-fg-3">
                <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-green-500 inline-block" /> 18.5</span>
                <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-yellow-500 inline-block" /> 25</span>
                <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-red-500 inline-block" /> 30</span>
              </div>
            )}
            <p className="mt-3 text-sm text-fg-3">Tap a metric above to chart it here.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default WeightChart;
